/*
 * @providesModule LiveBracketTimerUtils
 *
 */

import moment from 'moment';
import momenttz from 'moment-timezone';
import I18n, {_m} from 'INTL';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {Alert, Platform} from 'react-native';
import PushPermissionController from 'PushPermissionController';
import {registerNotifyUser} from '../APIs/notifyMe';
import Tournament from 'SKZTournament';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

export class LiveBracketTimerUtils {
  /**
   * Calculate remaining time for current round
   * @param tournament - The tournament object
   * @returns Remaining seconds for current round
   */
  static getRemainingTimeForCurrentRound(tournament: any): number {
    // Check if tournament and bracket exist
    if (!tournament || !tournament.bracket) {
      console.debug('LiveBracketTimerUtils: tournament or bracket is null/undefined', {
        tournament: !!tournament,
        bracket: !!tournament?.bracket,
      });
      return 0;
    }

    const roundStartTime = new Date(tournament.bracket.current_round_start_time).getTime();
    const now = Date.now();
    const roundDuration = tournament.bracket.round_length_seconds * 1000;
    const roundEndTime = roundStartTime + roundDuration;
    const remainingSeconds = Math.max(0, Math.floor((roundEndTime - now) / 1000));

    return remainingSeconds;
  }

  /**
   * Format timer string from DD:HH:MM:SS to appropriate format for live brackets
   * - More than 24 hours: Shows date format like "7th Oct 10 (Time zone)"
   * - Less than 24 hours: Shows time format like "10h 16m 16s"
   * @param timeUntil - Timer string in DD:HH:MM:SS format
   * @returns Formatted timer string
   */
  static formatLiveBracketTimer(timeUntil: string): string {
    if (!timeUntil) return '';

    // Parse DD:HH:MM:SS format
    const parts = timeUntil.split(':');
    if (parts.length !== 4) return timeUntil;

    const days = parseInt(parts[0] || '0', 10);
    const hours = parseInt(parts[1] || '0', 10);
    const minutes = parseInt(parts[2] || '0', 10);
    const seconds = parseInt(parts[3] || '0', 10);

    // Calculate total hours
    const totalHours = days * 24 + hours;

    // If more than 24 hours, show date format
    if (totalHours >= 24) {
      // Calculate target date
      const now = new Date();
      const targetDate = new Date(
        now.getTime() +
          days * 24 * 60 * 60 * 1000 +
          hours * 60 * 60 * 1000 +
          minutes * 60 * 1000 +
          seconds * 1000
      );

      // Format as "7th Oct 10 (Time zone)"
      const day = targetDate.getDate();
      const month = targetDate.toLocaleString('en', {month: 'short'});
      const year = targetDate.getFullYear().toString().slice(-2);
      const timeZone = targetDate.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop();

      const dayWithSuffix = this._getDayWithSuffix(day);
      return `${dayWithSuffix} ${month} ${year} (${timeZone})`;
    } else {
      // Less than 24 hours, show time format like "10h 16m 16s" or "16m 16s" if no hours
      const displayHours = totalHours;
      const displayMinutes = minutes;
      const displaySeconds = seconds;

      if (displayHours > 0) {
        const result = `${displayHours}h ${displayMinutes.toString().padStart(2, '0')}m ${displaySeconds.toString().padStart(2, '0')}s`;

        return result;
      } else {
        const result = `${displayMinutes.toString().padStart(2, '0')}m ${displaySeconds.toString().padStart(2, '0')}s`;
        return result;
      }
    }
  }

  /**
   * Helper function to add ordinal suffix to day number
   * @param day - Day number
   * @returns Day with suffix (1st, 2nd, 3rd, 4th, etc.)
   */
  private static _getDayWithSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  }

  /**
   * Format time in MM:SS format with M and S suffixes
   * @param seconds - Time in seconds
   * @returns Formatted time string (e.g., "05M:30S")
   */
  static formatTimeMMSS(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}M:${remainingSeconds.toString().padStart(2, '0')}S`;
  }

  /**
   * Get the appropriate timer text for live bracket match select cells
   * @param tournament - The tournament object
   * @param timeUntil - Current time until value from state
   * @returns Formatted timer text string
   */
  static getLiveBracketTimerText(tournament: any, timeUntil?: string): string {
    const now = moment();

    // Get the registration time periods
    const showStartTime = moment(tournament.bracket?.show_start_time);
    const registerStartTime = moment(tournament.bracket?.register_start_time);
    const registerEndTime = moment(tournament.bracket?.register_end_time);

    // Check if bracket times are valid
    const hasValidBracketTimes =
      showStartTime.isValid() &&
      registerStartTime.isValid() &&
      registerEndTime.isValid() &&
      !registerStartTime.isSame(registerEndTime);

    // Special case: when show_start_time equals register_start_time, registration is already open
    const isRegistrationOpen = showStartTime.isSame(registerStartTime);

    if (!hasValidBracketTimes) {
      // Fallback to original logic when bracket times are invalid
      const timeUntilValue = timeUntil || '';
      const formattedTime = this.formatLiveBracketTimer(timeUntilValue);

      // Check if we can determine if it's more than 24 hours away from the timeUntil string
      // If timeUntil contains days (format: DD:HH:MM:SS), check if days > 0
      const hasDays =
        timeUntilValue &&
        timeUntilValue.includes(':') &&
        parseInt(timeUntilValue.split(':')[0] || '0') > 0;
      const registrationText = hasDays ? '' : _m('Opens in: {0}', formattedTime);
      return registrationText;
    }

    // Handle special case where show_start_time equals register_start_time
    if (isRegistrationOpen) {
      if (now.isBefore(registerEndTime)) {
        // Registration is open, show time until registration ends
        const timeUntilEnd = I18n.daysHrsMinsSecsUntil(registerEndTime.toDate().toISOString());
        const formattedTime = this.formatLiveBracketTimer(timeUntilEnd);
        const isPlayerRegistered = tournament.isPlayerRegistered();
        return isPlayerRegistered
          ? _m('Round 1 starts in: {0}', formattedTime)
          : _m('Closes in: {0}', formattedTime);
      } else {
        // Registration has ended
        return _m('Registration Closed');
      }
    }

    // Check which period we're currently in
    if (now.isBefore(showStartTime)) {
      // Before show_start_time - show "Registration Starts on: [date] [time]"
      const timeDiff = registerStartTime.diff(now);
      const hoursUntilStart = timeDiff / (1000 * 60 * 60);

      if (hoursUntilStart >= 24) {
        return '';
      } else {
        const timeUntilValue = timeUntil || '';
        const formattedTime = this.formatLiveBracketTimer(timeUntilValue);
        return _m('Opens in: {0}', formattedTime);
      }
    } else if (now.isBetween(showStartTime, registerStartTime)) {
      // Between show_start_time and register_start_time - show "Opens in: [countdown]"
      const timeUntilValue = timeUntil || '';
      const formattedTime = this.formatLiveBracketTimer(timeUntilValue);

      // Check if registration start time is more than 24 hours away
      const timeDiff = registerStartTime.diff(now);
      const hoursUntilStart = timeDiff / (1000 * 60 * 60);
      const registrationText = hoursUntilStart >= 24 ? '' : _m('Opens in: {0}', formattedTime);
      return registrationText;
    } else if (now.isBetween(registerStartTime, registerEndTime)) {
      const timeUntilEnd = I18n.daysHrsMinsSecsUntil(registerEndTime.toDate().toISOString());
      const formattedTime = this.formatLiveBracketTimer(timeUntilEnd);
      const isPlayerRegistered = tournament.isPlayerRegistered();
      return isPlayerRegistered
        ? _m('Round 1 starts in: {0}', formattedTime)
        : _m('Closes in: {0}', formattedTime);
    } else {
      // After register_end_time - show appropriate message
      return _m('Registration Closed');
    }
  }

  /**
   * Get the appropriate button text for live bracket tournaments
   * @param tournament - The tournament object
   * @returns Button text string
   */
  static getLiveBracketButtonText(tournament: any): string {
    // live brackets - tournament started and user is registered
    if (
      tournament.hasTournamentStarted() &&
      tournament.isPlayerRegistered() &&
      tournament.isLiveBracket()
    ) {
      // Check if user is waiting for next round
      if (tournament.isPlayerWaitingForNextRound()) {
        // Get the current round number from bracket data
        const currentRound = tournament.bracket?.current_round || 1;

        // Only show "starts soon" for Round 1, all other rounds show "is live"
        if (currentRound === 1) {
          // Check if we're within 5 minutes of Round 1 starting
          if (tournament.bracket?.current_round_start_time) {
            const secondsToShowLiveBracketToast =
              ClientPropertyConstants.getLiveBracketsShowToastBeforeStartSeconds();
            const nextRoundStartTime = moment(tournament.bracket.current_round_start_time);
            const remainingTimeToNextRound = nextRoundStartTime.diff(moment(), 'seconds');

            if (
              remainingTimeToNextRound <= secondsToShowLiveBracketToast &&
              remainingTimeToNextRound > 0
            ) {
              return _m('Round {0}\nstarts soon', currentRound);
            }
          }
        }

        // For Round 1 (if not within 5 minutes) or all other rounds, show "is live"
        return _m('Round {0}\nis live', currentRound);
      }
    }

    // Fallback to default logic
    return '';
  }

  /**
   * Check if timer text should have mixed colors (prefix black, time red)
   * @param description - The description text
   * @param tournament - The tournament object
   * @returns Boolean indicating if text should have mixed colors
   */
  static shouldApplyMixedColors(description: string, tournament: any): boolean {
    return (
      tournament.isLiveBracket() &&
      (description.includes('Opens in:') ||
        description.includes('Closes in:') ||
        description.includes('Round 1 starts in:'))
    );
  }

  /**
   * Split timer text into prefix and time parts for mixed color rendering
   * @param description - The description text
   * @returns Object with prefix and timePart
   */
  static splitTimerText(description: string): {prefix: string; timePart: string} {
    const parts = description?.split(': ');
    const prefix = parts?.[0] + ': '; // "Opens in: "
    const timePart = parts?.[1] || ''; // "00:00:13:37"

    if (!description) {
      return {prefix: '', timePart: ''};
    }

    return {prefix, timePart};
  }

  /**
   * Check if the notify button should be shown for live brackets
   * @param tournament - The tournament object
   * @returns Boolean indicating if notify button should be shown
   */
  static shouldShowNotifyButton(tournament: any): boolean {
    const isLiveBracket = tournament.isLiveBracket();
    const shouldShowLiveBracket = tournament.shouldShowLiveBracket();
    const hasTournamentStarted = tournament.hasTournamentStarted();
    const isPlayerRegistered = tournament.isPlayerRegistered();
    const isRegistrationAvailable = tournament.isRegistrationAvailable();

    // Check if show_start_time is present
    const hasShowStartTime = Boolean(
      tournament.bracket?.show_start_time && moment(tournament.bracket.show_start_time).isValid()
    );

    // Check the to_notify_register_start field from BE - show notify button when this is false or undefined
    const shouldNotifyRegisterStart = tournament.bracket?.to_notify_register_start !== true;

    const shouldShow =
      isLiveBracket &&
      shouldShowLiveBracket &&
      !hasTournamentStarted &&
      !isPlayerRegistered &&
      !isRegistrationAvailable && // Don't show notify button if registration is currently available
      hasShowStartTime && // Only show notify button if show_start_time is present
      shouldNotifyRegisterStart; // Show notify button when to_notify_register_start is false

    return shouldShow;
  }

  /**
   * Determines if registration is currently open and available for the user to register
   * for a live bracket tournament.
   *
   * @param tournament - The tournament to check
   */
  static isRegistrationAvailableForUser(tournament: Tournament): boolean {
    return (
      tournament.isLiveBracket() &&
      !tournament.hasTournamentStarted() &&
      !tournament.isPlayerRegistered() &&
      tournament.isRegistrationAvailable()
    );
  }

  /**
   * Determines the current interaction state for a live bracket tournament.
   * Used for tracking and UI state management.
   *
   * @param tournament - The tournament to check
   * @returns 'notify' | 'register' | 'details' - The current state based on timing and user eligibility
   */
  static getCurrentLiveBracketState(tournament: Tournament): string {
    if (LiveBracketTimerUtils.shouldShowNotifyButton(tournament)) {
      return 'notify';
    } else if (LiveBracketTimerUtils.isRegistrationAvailableForUser(tournament)) {
      return 'register';
    }
    return 'details';
  }

  /**
   * Determines whether the live bracket registration has started or not.
   * Uses the tournament.bracket.register_start_time field.
   *
   * @param tournament - The tournament object.
   * @returns boolean - True if registration has started, false otherwise.
   */
  static hasLiveBracketRegistrationStarted(registerStartTime: string | undefined | null): boolean {
    if (!registerStartTime) {
      return false;
    }
    // Use moment to check if now is after or at register_start_time
    return moment().isSameOrAfter(moment(registerStartTime));
  }

  /**
   * Handle notify button tap - navigates to event details page
   * @param onPress - The onPress callback function
   * @param tournament - The tournament object
   * @param displayOnly - Whether the component is in display only mode
   * @param stopScalingAnimation - Function to stop scaling animation
   */
  static handleNotifyButtonTap(
    onPress: (tournament: any) => void,
    tournament: any,
    displayOnly: boolean,
    stopScalingAnimation: () => void
  ): void {
    if (displayOnly) {
      return;
    }

    stopScalingAnimation();
    // Track notify button tap event
    // Navigate to event details page (same as register button)
    onPress(tournament);
  }

  /**
   * Request notification permission with platform-specific handling
   * @param callNotifyAPI - Function to call the notify API
   */
  static requestNotificationPermission(callNotifyAPI: () => void): void {
    // First check if permissions are already granted
    PushPermissionController.getStatus((status: string) => {
      const hasNotificationAccess = status === PushPermissionController.Status.Authorized;
      trackTelemetryEvent({
        event: TelemetryEventName.LIVE_BRACKET_NOTIFICATION_CHECK,
        payload: {
          notification_access_flag: hasNotificationAccess ? 1 : 0,
        },
      });
      if (status === PushPermissionController.Status.Authorized) {
        // Permissions already granted, directly call the API
        callNotifyAPI();
        return;
      }

      // Permissions not granted, show the hard prompt
      PushPermissionController.displayHardPrompt((newStatus: string) => {
        if (newStatus === PushPermissionController.Status.Authorized) {
          callNotifyAPI();
          trackTelemetryEvent({
            event: TelemetryEventName.LIVE_BRACKET_PUSH_NOTIF_GRANTED,
          });
        } else {
          Alert.alert(
            _m('Enable Notifications'),
            _m(
              'Notifications are currently disabled. Please go to Settings > Notifications to enable them for this app.'
            ),
            [
              {
                text: _m('Cancel'),
                style: 'cancel',
              },
              {
                text: _m('Open Settings'),
                onPress: () => {
                  PushPermissionController.openSettings();
                },
              },
            ]
          );
        }
      });
    });
  }

  /**
   * Call the notify API and handle success/error states
   * @param tournament - The tournament object
   * @param setShowSnackbar - Function to set show snackbar state
   * @returns Promise that resolves when API call is complete
   */
  static async callNotifyAPI(
    tournament: any,
    setShowSnackbar: (value: boolean) => void
  ): Promise<void> {
    // Call the new notify API with bracket ID
    const bracketId = tournament.bracket?.id;

    if (!bracketId) {
      Alert.alert(_m('Error'), _m('Unable to register for notifications. Please try again.'), [
        {text: _m('OK')},
      ]);
      return;
    }

    try {
      await registerNotifyUser(bracketId);
      // Show success snackbar
      setShowSnackbar(true);
    } catch (error) {
      // Show error message
      Alert.alert(_m('Error'), _m('Failed to enable notifications. Please try again.'), [
        {text: _m('OK')},
      ]);
    }
  }

  /**
   * Validates multiple moment objects
   * @param moments - Array of moment objects to validate
   * @returns Boolean indicating if all moments are valid
   */
  static validateMoments(...moments: moment.Moment[]): boolean {
    return moments.every((momentObj) => momentObj.isValid());
  }

  /**
   * Creates moment objects from date strings and validates them
   * @param dateStrings - Array of date strings to convert to moments
   * @returns Object containing created moments and validation result
   */
  static createAndValidateMoments(dateStrings: (string | null | undefined)[]) {
    const moments = dateStrings.map((dateString) => moment(dateString));
    const isValid = this.validateMoments(...moments);

    return {
      moments,
      isValid,
    };
  }

  /**
   * Validates bracket timing moments for live brackets
   * @param showStartTime - Show start time string
   * @param registerStartTime - Register start time string
   * @param registerEndTime - Register end time string
   * @returns Object containing validated moments and validation result
   */
  static validateBracketTimingMoments(
    showStartTime: string | null | undefined,
    registerStartTime: string | null | undefined,
    registerEndTime: string | null | undefined
  ) {
    const {moments, isValid} = this.createAndValidateMoments([
      showStartTime,
      registerStartTime,
      registerEndTime,
    ]);

    return {
      showStartTime: moments[0],
      registerStartTime: moments[1],
      registerEndTime: moments[2],
      isValid,
    };
  }

  /**
   * Formats duration in seconds to a human-readable string
   * @param durationInSeconds - Duration in seconds
   * @returns Formatted duration string (e.g., "30m", "1h 30m")
   */
  static formatDuration(durationInSeconds: number): string {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    if (hours < 1 && minutes >= 0) {
      return `${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  }

  /**
   * Converts an ISO date string to a formatted time string using moment-timezone.
   * - If date is today: returns '1PM IST'
   * - Else: returns '1PM IST, 27th Oct'
   */
  static formatShortDateTime(dateString: string): string {
    if (!dateString) return '';

    // Get user's timezone
    const userTimezone = momenttz.tz.guess();

    // Convert date to user's timezone
    const date = momenttz.tz(dateString, userTimezone);
    const now = momenttz.tz(userTimezone);

    if (!date.isValid()) return '';

    // Check if same day
    const isSameDay = date.isSame(now, 'day');

    // Format time without minutes if it's on the hour, otherwise include minutes
    const minutes = date.minutes();
    const timeFormat = minutes === 0 ? 'hA' : 'h:mmA';
    const timeStr = date.format(timeFormat);

    // Get timezone abbreviation
    const timezone = date.format('z');

    if (isSameDay) {
      return `${timeStr} ${timezone}`;
    }

    // Format date with day suffix
    const dayStr = date.format('Do');
    const month = date.format('MMM').toUpperCase();
    return `${timeStr} ${timezone}, ${dayStr} ${month}`;
  }

  static formatRegistrationTime(tournament: Tournament): string {
    const registerStartTime = tournament?.bracket?.register_start_time;

    if (!registerStartTime) {
      return '';
    }

    // Only show banner if user is in notify stage (not registered and registration hasn't started)
    const isPlayerRegistered = tournament.isPlayerRegistered();
    const hasRegistrationStarted = this.hasLiveBracketRegistrationStarted(registerStartTime);

    // Don't show banner if user is already registered or registration has started
    if (isPlayerRegistered || hasRegistrationStarted) {
      return '';
    }

    // Get user's timezone
    const userTimezone = momenttz.tz.guess();

    // Convert registration time to user's timezone
    const startTime = momenttz.tz(registerStartTime, userTimezone);
    const now = momenttz.tz(userTimezone);

    // Get timezone abbreviation
    const timezone = startTime.format('z');

    // Check if it's today
    if (startTime.isSame(now, 'day')) {
      const time = startTime.format('h:mm A');
      return _m(`Registration Starts Today ${time} ${timezone}`);
    }

    // Check if it's tomorrow
    if (startTime.isSame(now.clone().add(1, 'day'), 'day')) {
      const time = startTime.format('h:mm A');
      return _m(`Registration Starts Tomorrow ${time} ${timezone}`);
    }

    // Otherwise show full date
    const date = startTime.format('Do MMM');
    const time = startTime.format('h:mm A');
    return _m(`Registration Starts ${date} ${time} ${timezone}`);
  }
}

export default LiveBracketTimerUtils;
